/**
 * Which tabs a workbench project shows, and in what order.
 *
 * Keyed on the project's type so a new type is a registry entry rather than a change to the
 * route module — the reason the backend seeds the whole taxonomy while the UI implements two
 * of it (D8).
 */

import { TLProjectType } from '@/api/gen/tessera-backend-api.schemas';

export enum ProjectTab {
  PROGRAMS = 'programs',
  OBJECTS = 'objects',
  SPECS = 'specs',
  RULES_AND_SKILLS = 'rules-skills',
  LANDSCAPE_SYNC = 'landscape-sync',
  SETTINGS = 'settings',
  MEMBERS = 'members',
}

/**
 * A tab set is non-empty by construction. `resolveProjectTab` falls back to `tabs[0]`, which
 * would be `undefined` for an empty entry — a lie the `ProjectTab` return type would not catch.
 * As a tuple, an empty registry entry is a compile error instead.
 */
type TabSet = readonly [ProjectTab, ...ProjectTab[]];

// SPECS is hidden until the Functional Specs surface ships; resolveProjectTab
// sends old ?tab=specs links back to the first tab (Programs).
const GENERAL_TABS: TabSet = [
  ProjectTab.PROGRAMS,
  ProjectTab.RULES_AND_SKILLS,
  ProjectTab.LANDSCAPE_SYNC,
  ProjectTab.MEMBERS,
];

/**
 * No Programs and no Landscape Sync, which is the point of the entry rather than an omission.
 *
 * A Code Modernization project has no local programs — its objects come from SAP and live in the
 * remedy behind it — so a Programs tab would offer an upload that has nowhere to go. Landscape
 * sync is a system-to-system concern (comparing and retrofitting between landscapes) rather than
 * work over a set of objects, so it does not belong under a project at all.
 *
 * Rules & Skills is here because agentic rule resolution reads the project's own `rules.md`, and
 * this tab is its only editor; without it the file that decides what instructions reach a run
 * would be uneditable. It is already project-scoped (`domain=PROJECTS`, `correlation_id`), so it
 * needs nothing beyond the registry entry.
 *
 * No Editor tab: the editor is a route (`/editor/PROJECTS/{projectId}`), reached the way the
 * Programs tab reaches it. That pair is what the editor store keys persisted tabs on, so a tab
 * here would give the same surface a second identity.
 */
const CODE_MODERNIZATION_TABS: TabSet = [
  ProjectTab.OBJECTS,
  ProjectTab.RULES_AND_SKILLS,
  ProjectTab.SETTINGS,
  ProjectTab.MEMBERS,
];

/**
 * Partial on purpose: a type absent here falls back to the GENERAL set, so the backend can add a
 * type without the frontend having to ship in the same release.
 */
const TABS_BY_TYPE: Partial<Record<TLProjectType, TabSet>> = {
  [TLProjectType.GENERAL]: GENERAL_TABS,
  [TLProjectType.CODE_MODERNIZATION]: CODE_MODERNIZATION_TABS,
};

/**
 * The tabs to render for a project of `type`.
 *
 * Falls back to the GENERAL set for three distinct cases, all of which are reachable: a type the
 * backend seeded but the UI has no layout for yet; a type this client's enum predates, which is
 * what a backend deployed ahead of the frontend produces; and no type at all, which is how a
 * project row written before the Phase 1 migration reads back.
 */
export function tabsForProjectType(type: TLProjectType | undefined): TabSet {
  return (type && TABS_BY_TYPE[type]) ?? GENERAL_TABS;
}

/**
 * The tab to actually show, given what the URL asked for.
 *
 * A `?tab=` naming a tab this project's type does not offer would otherwise render no active
 * trigger and no content — an empty panel with no error. This became load-bearing the moment the
 * Code Modernization set arrived: `?tab=programs` on a CM project is a real URL, from a bookmark
 * made before the entry existed or a hand-edited link.
 */
export function resolveProjectTab(tabs: TabSet, requested: ProjectTab | undefined): ProjectTab {
  return requested && tabs.includes(requested) ? requested : tabs[0];
}
